import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, Image, TouchableOpacity, StyleSheet, Dimensions } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { getScaledValue, isFactorTv } from '@rnv/renative';
import { ChildStackParamList } from '../../navigation/types';
import { useAuthStore } from '../../state/authStore';
import useKeydown from '../../hooks/useKeydown';
import { contentApi, VideoListItem } from '../../api/content';

type Props = NativeStackScreenProps<ChildStackParamList, 'Library'>;

const COLS = 4;

// Compute thumb dimensions at module load instead of using CSS `aspect-ratio`
// (Chrome 88+, not on our TV floor Chromium 76). Approximates the card width
// from window width minus padding and per-card margin (6 each side).
const _w = Dimensions.get('window').width;
const _cardWidth = Math.floor((_w - 40 - COLS * 12) / COLS);
const _thumbHeight = Math.floor((_cardWidth * 9) / 16);

export default function LibraryScreen({ navigation, route }: Props) {
  const { childId } = route.params;
  const { accessToken } = useAuthStore();
  const [videos, setVideos] = useState<VideoListItem[]>([]);
  const [focused, setFocused] = useState(0);

  useEffect(() => {
    if (accessToken) {
      contentApi.list({}, accessToken).then((r) => setVideos(r.videos));
    }
  }, [accessToken]);

  useKeydown({
    isActive: true,
    left: () => setFocused((f) => Math.max(0, f - 1)),
    right: () => setFocused((f) => Math.min(videos.length - 1, f + 1)),
    up: () => setFocused((f) => Math.max(0, f - COLS)),
    down: () => setFocused((f) => Math.min(videos.length - 1, f + COLS)),
    ok: () => {
      const video = videos[focused];
      if (video) navigation.navigate('Player', { childId, videoId: video.id });
    },
    back: () => navigation.goBack(),
  });

  return (
    <View style={styles.container}>
      <Text style={styles.title}>All Videos</Text>
      <FlatList
        data={videos}
        numColumns={COLS}
        keyExtractor={(v) => v.id}
        renderItem={({ item, index }) => (
          <TouchableOpacity
            onPress={() => navigation.navigate('Player', { childId, videoId: item.id })}
            style={[styles.card, isFactorTv && focused === index && styles.cardFocused]}
          >
            <Image source={{ uri: item.thumbUrl }} style={styles.thumb} />
            <Text style={styles.cardTitle} numberOfLines={2}>{item.title}</Text>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#FFF8F0', padding: getScaledValue(20) },
  title: { fontSize: getScaledValue(28), fontWeight: 'bold', color: '#333', marginBottom: getScaledValue(20) },
  card: {
    flex: 1,
    margin: getScaledValue(6),
    borderRadius: getScaledValue(10),
    backgroundColor: '#fff',
    overflow: 'hidden',
    borderWidth: 3,
    borderColor: 'transparent',
  },
  cardFocused: { borderColor: '#FF8C42' },
  thumb: { width: '100%', height: _thumbHeight },
  cardTitle: { padding: getScaledValue(6), fontSize: getScaledValue(12), color: '#333' },
});
